import React, { ReactNode, useState } from "react";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { SxProps, Theme } from "@mui/material/styles";

import Button, { ButtonProps } from "components/Button";

import { ReactComponent as CloseIcon } from "assets/images/icon_close_modal.svg";

import { styles } from "./styles";

interface StyledModalProps {
  isOpen: boolean;
  title?: string;
  subtitle?: string;
  content: ReactNode;
  sx?: SxProps<Theme>;
  closeBtnText?: string;
  closeBtnProps?: ButtonProps;
  actionBtnText?: string;
  actionBtnColor?: "primary" | "secondary" | "error";
  actionBtnBorder?: boolean;
  actionBtnProps?: ButtonProps;

  onClose: () => void;
  onAction?: () => Promise<void>;
  onActionError?: () => Promise<void>;
}
// TODO: test all component usages
const StyledModal: React.FC<StyledModalProps> = (props) => {
  const {
    isOpen,
    title,
    subtitle,
    content,
    sx,
    closeBtnText,
    closeBtnProps,
    actionBtnText,
    actionBtnColor,
    actionBtnBorder,
    actionBtnProps,
    onClose,
    onAction,
    onActionError,
  } = props;

  const [loading, setLoading] = useState(false);

  const onCloseModal = () => {
    onClose();
    setLoading(false);
  };

  const onActionClick = async () => {
    setLoading(true);
    try {
      await onAction?.();
    } catch (error) {
      await onActionError?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseModal}
      slotProps={{ paper: { sx: { ...styles.modal, ...(sx ?? {}) } } }}
    >
      {(title || subtitle) && (
        <DialogTitle component="div" sx={{ pt: 0, pr: 4, pb: 1.5, pl: 2 }}>
          {title && <Typography variant="h5">{title}</Typography>}
          {subtitle && (
            <Typography sx={{ mt: 0.5 }} variant="body1" color="grey700">
              {subtitle}
            </Typography>
          )}
        </DialogTitle>
      )}

      <IconButton
        aria-label="close-modal"
        onClick={onCloseModal}
        sx={styles.closeButton}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          px: 2,
          py: 1.5,
          // TODO: test on Windows(Chrome, Edge, Mozilla); on mac(Mozilla)
          scrollbarWidth: "none",
        }}
      >
        {content}
      </DialogContent>
      <DialogActions sx={{ py: 0, px: 2 }}>
        <Box
          sx={{
            width: "100%",
            mt: 1.5,
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            color="secondary"
            variant="contained"
            onClick={onCloseModal}
            disabled={loading}
            sx={{ width: { xs: "100%", sm: "auto" } }}
            {...closeBtnProps}
            loading={false}
          >
            {closeBtnText || "Скасувати"}
          </Button>
          {onAction && (
            <Button
              {...actionBtnProps}
              color={actionBtnColor || "primary"}
              variant="contained"
              onClick={onActionClick}
              withBorder={!!actionBtnBorder}
              sx={{ width: { xs: "100%", sm: "auto" } }}
              loading={loading}
            >
              {actionBtnText || "Зберегти"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default StyledModal;
